import { useCallback, useEffect } from "react";
import { spaceActions } from "redux/Ldb/actions/spaceActions";
import { useAppDispatch, useAppSelector } from "../storeHooks";
import { SpaceGuestTokenVerifyPayload } from "redux/Ldb/types/spaceTypes";

const useSpaceGuestTokenVerify = (
    { token }: { token: string },
    options: { skip?: boolean; unwrap?: boolean }
): useSpaceGuestTokenVerifyReturn => {
    const dispatch = useAppDispatch();

    const {
        spaceGuestTokenVerifying,
        spaceGuestTokenVerifiedSuccess,
        spaceGuestTokenVerifiedFailure,
    } = useAppSelector((state) => state.space);

    async function spaceGuestTokenVerify(data: SpaceGuestTokenVerifyPayload): Promise<any> {
        return new Promise((resolve, reject) => {
            // Reset any previous state
            dispatch(spaceActions.resetSpaceGuestTokenVerify());

            // Start the Redux action
            dispatch(spaceActions.spaceGuestTokenVerify(data));

            // Poll for state changes
            const checkState = () => {
                const currentState = dispatch((dispatch, getState) => {
                    const state = getState();
                    return {
                        isLoading: state.space.spaceGuestTokenVerifying,
                        success: state.space.spaceGuestTokenVerifiedSuccess,
                        error: state.space.spaceGuestTokenVerifiedFailure,
                    };
                });

                if (currentState.error) {
                    reject("Error verifying guest login");
                } else if (currentState.success && !currentState.isLoading) {
                    resolve(currentState.success);
                } else if (
                    !currentState.isLoading &&
                    !currentState.success &&
                    !currentState.error
                ) {
                    reject("Request failed without error or success response");
                } else {
                    // Still loading, check again
                    setTimeout(checkState, 100);
                }
            };

            checkState();
        });
    }

    const resetSpaceGuestTokenVerify = useCallback(() => {
        dispatch(spaceActions.resetSpaceGuestTokenVerify());
    }, [dispatch]);

    async function verifySpaceGuestToken() {
        await spaceGuestTokenVerify({ token })
            .then((data) => {
                console.log({ data });
            })
            .catch((error) => {
                console.error(error);
            });
    }

    useEffect(() => {
        if (options.skip) return;

        if (options.unwrap) {
            spaceGuestTokenVerify({ token });
        } else {
            verifySpaceGuestToken();
        }
    }, []);

    return {
        isSuccess: Boolean(spaceGuestTokenVerifiedSuccess),
        isError: !!spaceGuestTokenVerifiedFailure,
        isLoading: spaceGuestTokenVerifying,
        data: spaceGuestTokenVerifiedSuccess,
        error: spaceGuestTokenVerifiedFailure,
        resetSpaceGuestTokenVerify,
    };
};

export default useSpaceGuestTokenVerify;

type useSpaceGuestTokenVerifyReturn = {
    isSuccess: boolean;
    isError: boolean;
    isLoading: boolean;
    data: any;
    error: any;
    resetSpaceGuestTokenVerify: () => void;
};
